import { FormEvent, useEffect, useState } from "react";
import { CheckCircle2, RotateCcw, Save } from "lucide-react";
import { MapSelector } from "../../map/components/MapSelector";
import { BAGHDAD_CITIES, IRAQ_GOVERNORATES, SHOP_CLASSES } from "../constants";
import {
  emptyManualForm,
  toManualFormValues,
  validateManualShop,
  type ManualShopFormValues,
  type ManualShopValidation
} from "../schemas/shopSchema";
import type { Shop, ShopCreateInput } from "../types";

type ShopFormProps = {
  editingShop: Shop | null;
  onSubmit: (input: ShopCreateInput, editingShop: Shop | null) => Promise<void>;
  onCancelEdit: () => void;
};

export function ShopForm({ editingShop, onSubmit, onCancelEdit }: ShopFormProps) {
  const [values, setValues] = useState<ManualShopFormValues>(() => emptyManualForm());
  const [errors, setErrors] = useState<ManualShopValidation["errors"]>({});
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    setValues(editingShop ? toManualFormValues(editingShop) : emptyManualForm());
    setErrors({});
    setSuccessMessage(null);
  }, [editingShop]);

  const updateField = <Key extends keyof ManualShopFormValues>(
    key: Key,
    value: ManualShopFormValues[Key]
  ) => {
    setValues((current) => {
      if (key === "shopGovenorate" && value !== "بغداد") {
        return {
          ...current,
          [key]: value,
          shopCity: ""
        };
      }

      return {
        ...current,
        [key]: value
      };
    });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSuccessMessage(null);

    const result = validateManualShop(values);
    setErrors(result.errors);

    if (!result.data) {
      return;
    }

    setIsSaving(true);

    try {
      await onSubmit(result.data, editingShop);
      setSuccessMessage(editingShop ? "Shop updated." : "Shop added.");

      if (!editingShop) {
        setValues(emptyManualForm());
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form className="entry-grid" onSubmit={handleSubmit}>
      <section className="form-panel" aria-label="Shop information">
        <div className="section-heading">
          <div>
            <p className="eyebrow">{editingShop ? "Edit shop" : "Manual entry"}</p>
            <h2>{editingShop ? editingShop.shopName : "Add cosmetic shop"}</h2>
          </div>
          {editingShop ? (
            <button type="button" className="ghost-button compact-button" onClick={onCancelEdit}>
              <RotateCcw size={16} />
              Cancel edit
            </button>
          ) : null}
        </div>

        <div className="form-grid">
          <label className="field wide-field">
            <span>Shop name *</span>
            <input
              value={values.shopName}
              onChange={(event) => updateField("shopName", event.target.value)}
              placeholder="Example: Marmara Cosmetics"
              autoComplete="organization"
            />
            {errors.shopName ? <span className="field-error">{errors.shopName}</span> : null}
          </label>

          <label className="field">
            <span>Phone number</span>
            <input
              value={values.shopPhoneNumber}
              onChange={(event) => updateField("shopPhoneNumber", event.target.value)}
              placeholder="07xx xxx xxxx"
              inputMode="tel"
              autoComplete="tel"
            />
          </label>

          <label className="field">
            <span>Class</span>
            <select
              value={values.shopClass}
              onChange={(event) => updateField("shopClass", event.target.value as ManualShopFormValues["shopClass"])}
            >
              <option value="">Not set</option>
              {SHOP_CLASSES.map((shopClass) => (
                <option key={shopClass} value={shopClass}>
                  Class {shopClass}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Governorate</span>
            <select
              value={values.shopGovenorate}
              onChange={(event) => updateField("shopGovenorate", event.target.value)}
            >
              <option value="">Not set</option>
              {IRAQ_GOVERNORATES.map((governorate) => (
                <option key={governorate} value={governorate}>
                  {governorate}
                </option>
              ))}
            </select>
          </label>

          {values.shopGovenorate === "بغداد" ? (
            <label className="field">
              <span>City</span>
              <select value={values.shopCity} onChange={(event) => updateField("shopCity", event.target.value)}>
                <option value="">Not set</option>
                {BAGHDAD_CITIES.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <label className="field">
            <span>District</span>
            <input
              value={values.shopDistrict}
              onChange={(event) => updateField("shopDistrict", event.target.value)}
              placeholder="District"
            />
          </label>

          <label className="field">
            <span>Street</span>
            <input
              value={values.shopStreet}
              onChange={(event) => updateField("shopStreet", event.target.value)}
              placeholder="Street"
            />
          </label>
        </div>

        <div className="form-actions">
          <button type="submit" className="primary-button" disabled={isSaving}>
            <Save size={17} />
            {isSaving ? "Saving..." : editingShop ? "Update shop" : "Save shop"}
          </button>

          {successMessage ? (
            <span className="success-message">
              <CheckCircle2 size={16} />
              {successMessage}
            </span>
          ) : null}
        </div>
      </section>

      <MapSelector
        value={values.shopLocation}
        error={errors.shopLocation}
        onChange={(location) => updateField("shopLocation", location)}
      />
    </form>
  );
}
